import { Injectable } from "@angular/core";
import { ToastrService, IndividualConfig } from "ngx-toastr";
import { ModalAlertService, ModalConfig } from "../../components/modal-alert/modal-alert.component";

// Interface para manter compatibilidade com código existente
interface SweetAlertOptions {
  title?: string;
  text?: string;
  html?: string;
  icon?: "warning" | "error" | "success" | "info" | "question";
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class SystemService {
  AUTH_PROVIDER: "cognito" | "grpc" = "grpc";
  constructor(
    private toastr: ToastrService,
    private modalService: ModalAlertService,
  ) {}

  showToast(
    message: string,
    title: string,
    type: "success" | "error" | "info" | "warning",
    options?: Partial<IndividualConfig>,
  ) {
    this.toastr[type](message, title, {
      positionClass: "toast-bottom-center",
      closeButton: true,
      timeOut: 3000,
      ...options,
    });
  }

  showAlert(options: SweetAlertOptions, confirmButtonText = "OK") {
    return new Promise<{ isConfirmed: boolean }>((resolve) => {
      this.modalService.show({
        icon: options.icon,
        title: options.title,
        description: options.text || options.html,
        mainButton: {
          text: confirmButtonText,
          action: () => {
            this.modalService.hide();
            resolve({ isConfirmed: true });
          },
        },
        closeOnBackdrop: true,
      });
    });
  }

  showSuccessAlert(message: string, title: string = "Sucesso!") {
    return this.showAlert({
      title,
      text: message,
      icon: "success",
    });
  }

  showWarningAlert(message: string, title: string = "Atenção!") {
    return this.showAlert({
      title,
      text: message,
      icon: "warning",
    });
  }

  showErrorAlert(message: string, title: string = "Atenção!") {
    return this.showAlert({
      title,
      text: message,
      icon: "error",
    });
  }

  showInfoAlert(message: string, title: string = "Atenção") {
    return this.showAlert({
      title,
      text: message,
      icon: "info",
    });
  }

  /**
   * Exibe um alerta de confirmação com botões de confirmar e cancelar
   * @param options Opções do alerta
   * @param confirmButtonText Texto do botão de confirmação
   * @param cancelButtonText Texto do botão de cancelamento
   * @returns Promise com o resultado da confirmação
   */
  showConfirmAlert(options: SweetAlertOptions, confirmButtonText = "Confirmar", cancelButtonText = "Cancelar") {
    return new Promise<{ isConfirmed: boolean; isDenied: boolean; isDismissed: boolean }>((resolve) => {
      this.modalService.show({
        icon: options.icon,
        title: options.title,
        description: options.text || options.html,
        mainButton: {
          text: confirmButtonText,
          action: () => {
            this.modalService.hide();
            resolve({ isConfirmed: true, isDenied: false, isDismissed: false });
          },
        },
        secondaryButton: {
          text: cancelButtonText,
          action: () => {
            this.modalService.hide();
            resolve({ isConfirmed: false, isDenied: true, isDismissed: false });
          },
        },
        closeOnBackdrop: true,
      });
    });
  }

  showSuccessToast(message: string, title: string = "Sucesso!") {
    this.showToast(message, title, "success");
  }

  showWarningToast(message: string, title: string = "Atenção!") {
    this.showToast(message, title, "warning");
  }

  showErrorToast(message: string, title: string = "Atenção!") {
    this.showToast(message, title, "error");
  }

  showInfoToast(message: string, title: string = "Atenção") {
    this.showToast(message, title, "info");
  }
}
